import { Button, Icon } from '@dev-spendesk/grapes';
import classNames from 'classnames';
import React from 'react';
import { useKey } from 'react-use';

import { useTranslation } from 'common/hooks/useTranslation';

type Props = {
  onBack?: () => void;
  onNext: () => void;
  onCancel?: () => void;
  backLabel?: string;
  nextLabel?: string;
  isNextDisabled?: boolean;
  isNextLoading?: boolean;
  hasNextIcon?: boolean;
  hasCancelButton?: boolean;
  canPressEnterForNext?: boolean;
  className?: string;
};

export function StepperFooter({
  onBack,
  onNext,
  onCancel,
  backLabel,
  nextLabel,
  isNextDisabled,
  isNextLoading,
  hasNextIcon = true,
  canPressEnterForNext,
  className,
}: Props) {
  const { t } = useTranslation('global');

  useKey(
    'Enter',
    () => {
      if (canPressEnterForNext) {
        onNext();
      }
    },
    {},
    [onNext, canPressEnterForNext],
  );

  return (
    <footer
      className={classNames(
        'border-border-default bg-background-primary-default fixed bottom-0 left-0 z-10 w-full border-0 border-t border-solid',
        className,
      )}
    >
      <div className="m-auto flex h-[80px] w-[920px] items-center justify-between">
        {onCancel && (
          <Button
            variant="secondaryNeutral"
            aria-label="cancel"
            onClick={onCancel}
            text={t('misc.cancel')}
          />
        )}
        {typeof onBack === 'function' && (
          <Button
            variant="secondaryNeutral"
            aria-label="back"
            onClick={onBack}
            text={backLabel || t('misc.back')}
            iconName="arrow-left"
          />
        )}
        <div className="flex-1" />
        <Button
          variant="primaryBrand"
          text={nextLabel || t('misc.next')}
          onClick={onNext}
          isDisabled={isNextDisabled}
          isLoading={isNextLoading}
          iconName={hasNextIcon ? 'arrow-right' : undefined}
          iconPosition="right"
          aria-label="next"
        />
        {canPressEnterForNext && (
          <span className="text-content-primary body-m ml-4 hidden sm:block">
            {t('misc.nextOnPress')}
            <Icon
              name="arrow-uturn-left-down"
              size="l"
              className="ml-8 rounded border border-solid p-4 align-middle"
            />
          </span>
        )}
      </div>
    </footer>
  );
}
